import { lazy, type ComponentType } from 'react';

import { registarFolha } from '@vestigo/core/i18n-da-tela';

/**
 * ── TODA TELA DESCE COM O DICIONÁRIO DELA ─────────────────────────────────
 *
 * `telaComDicionario` é o `lazy()` desta casa, e **o único**: nenhum ficheiro
 * de `apps/aluno/src` chama `lazy()` cru, e a cerca
 * `nenhuma-tela-desce-sem-o-dicionario.test.ts` falha se algum voltar a
 * chamar.
 *
 * ## O problema que ele resolve, e por que uma promessa solta não resolvia
 *
 * As 433 chaves do conteúdo do dicionário não viajam no pedaço de entrada
 * (a medição está em `packages/core/src/i18n/casca.ts`): das 533, o
 * `index-*.js` lê 100. As outras descem — e **quando** elas descem é a
 * diferença entre a tela certa e `sala.concluir` à vista do aluno.
 *
 * Disparar o `import()` do conteúdo no arranque do provedor de idioma seria
 * *quase* sempre suficiente: o pedido parte antes de o router pedir a tela, e
 * a folha é menor do que qualquer tela. Mas «quase sempre» aqui tem um caso
 * real — a tela em cache do navegador e a folha não, que é o que acontece na
 * primeira visita depois de um deploy que mudou o dicionário. O aluno veria
 * chaves cruas por um instante, numa tela que ninguém consegue reproduzir
 * depois.
 *
 * Aqui não há ordem a adivinhar: o `Promise.all` **espera as duas**, e o
 * `Suspense` que já existe à volta de toda tela mostra a mesma frase que
 * mostrava. O custo é zero quando a folha já desceu (o `import()` é guardado),
 * e quem abre só a porta da área — a vitrine, que é o que todo link do dono
 * abre — **nunca a baixa**.
 *
 * ## O pedido é guardado, e isso é o que o torna de graça
 *
 * `pedido` é a mesma promessa para todas as telas: a segunda tela não repete
 * o `import()` nem o `registarFolha`. E `registarFolha` é idempotente por
 * desenho, para o caso de duas telas resolverem no mesmo tick.
 */
let pedido: Promise<void> | null = null;

/** O conteúdo do dicionário, uma vez por sessão, registado ao chegar. */
export function folhaDoConteudo(): Promise<void> {
  pedido ??= import('@vestigo/core/i18n-conteudo')
    .then((m) => {
      registarFolha(m.CONTEUDO);
    })
    .catch(() => {
      /*
       * Pedaço que não desceu não pode derrubar a área: a tela desenha com a
       * casca, e o que faltar aparece como a chave. Deixar a tela inteira cair
       * por causa do dicionário seria trocar um defeito visível por uma área
       * fechada.
       */
    });
  return pedido;
}

/**
 * Uma tela sob demanda, que só desenha quando o dicionário dela já chegou.
 *
 * ## Os DOIS genéricos, e por que um só não serve
 *
 * `T` é o componente e `Props` existe só para o `T extends ComponentType<Props>`
 * ter onde se apoiar. Sem o segundo, o `tsc` tenta casar as props contra um
 * tipo fechado, não consegue, e cai num `ComponentType<never>` que produz
 * **27 erros de dez linhas cada** a falar de `getDerivedStateFromProps` — longe
 * da causa, e em ficheiros que ninguém tocou. Medido: 27 → 0 ao acrescentar
 * `Props`.
 *
 * O que isto preserva é a conferência das props na chamada:
 * `<SalaDeAula dados={…} />` continua a ser verificado como antes da troca.
 */
export function telaComDicionario<T extends ComponentType<Props>, Props = never>(
  carregar: () => Promise<{ default: T }>,
) {
  const comDicionario = async (): Promise<{ default: T }> => {
    /* Os dois pedidos partem antes do primeiro `await`: é isso que os põe na
       rede ao mesmo tempo, em vez de um depois do outro. */
    const pedacoDaTela = carregar();
    const folha = folhaDoConteudo();
    await folha;
    return pedacoDaTela;
  };
  return lazy(comDicionario);
}
